import { useCallback, useRef, useState } from 'react';
import type {
	RescheduleCabOption,
	RescheduleFareData,
} from '../../../-api/reschedule';
import { tripTracker } from '../../../-utils/tracker';
import type { RescheduleError, RescheduleStep } from '../index.types';

interface UseRescheduleFlowOptions {
	initialPickupDateTime: string;
}

export function useRescheduleFlow({
	initialPickupDateTime,
}: UseRescheduleFlowOptions) {
	const [step, setStep] = useState<RescheduleStep>('form');
	const [dateTime, setDateTime] = useState(initialPickupDateTime);
	const [fareData, setFareDataState] = useState<RescheduleFareData | null>(
		null,
	);
	const [selectedCab, setSelectedCab] = useState<RescheduleCabOption | null>(
		null,
	);
	const [error, setError] = useState<RescheduleError | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);

	const setFareData = useCallback((data: RescheduleFareData) => {
		setFareDataState(data);
		setStep('selecting-cab');
	}, []);

	const selectCab = useCallback(
		(cabId: string) => {
			const cab = fareData?.tripDetails.cabSelection.options.find(
				(c) => c.cabId === cabId,
			);
			if (cab) {
				tripTracker.tracker.rescheduleAmountReview({
					selectedDateTime: dateTime,
					selectedCab: cab,
				});
				setSelectedCab(cab);
				setStep('amount-due');
			}
		},
		[fareData, dateTime],
	);

	const startLoading = useCallback((target: 'loading-cabs' | 'processing') => {
		setStep(target);
	}, []);

	const failWith = useCallback((failError: RescheduleError) => {
		setError(failError);
		setStep('failure');
	}, []);

	const resetFlow = useCallback(() => {
		setStep('form');
		setDateTime(initialPickupDateTime);
		setFareDataState(null);
		setSelectedCab(null);
		setError(null);
	}, [initialPickupDateTime]);

	const goToStep = useCallback((target: RescheduleStep) => {
		setStep(target);
	}, []);

	const createAbortSignal = useCallback(() => {
		abortControllerRef.current = new AbortController();
		return abortControllerRef.current.signal;
	}, []);

	const cancel = useCallback(() => {
		abortControllerRef.current?.abort();
	}, []);

	return {
		state: { step, dateTime, fareData, selectedCab, error },
		transitions: {
			setDateTime,
			setFareData,
			selectCab,
			startLoading,
			failWith,
			resetFlow,
			goToStep,
		},
		abort: { createAbortSignal, cancel },
	};
}
